import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import useChangePhoto from '@/hooks/useChangePhoto';
import useGetProfile from '@/hooks/useGetProfile';
import useUpdateProfile from '@/hooks/useUpdateProfile';
import { Link, Mail, Phone } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import AvatarUploader from './AvatarUploader';

const LoanOfficerProfileForm = () => {
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [loanOfficerData, setLoanOfficerData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    licenseNumber: '',
    phoneNumber: '',
    emailAddress: '',
    website: '',
  });

  const { data: profileData, isLoading } = useGetProfile();
  const { mutate: updateProfile } = useUpdateProfile();

  useEffect(() => {
    if (profileData && !isLoading) {
      setLoanOfficerData({
        firstName: profileData.firstName || '',
        lastName: profileData.lastName || '',
        companyName: profileData.companyName || '',
        licenseNumber: profileData.licenseNumber || '',
        phoneNumber: profileData.phoneNumber || '',
        emailAddress: profileData.emailAddress || '',
        website: profileData.website || '',
      });
      if (profileData.photoUrl) {
        setProfileImage(profileData.photoUrl);
      }
    }
  }, [isLoading, profileData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoanOfficerData({
      ...loanOfficerData,
      [name]: value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile(loanOfficerData, {
      onSuccess: () => {
        toast.success('Profile saved successfully');
      },
    });
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}?loan-officer=${profileData?._id}`;

    navigator.clipboard
      .writeText(url)
      .then(() => {
        toast.success('Copied to clipboard!');
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
      });
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-2xl text-tax-deepBlue">
          Loan Officer Information
        </CardTitle>
        <CardDescription>
          Update your profile information and upload a professional photo
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex flex-col md:flex-row gap-6">
            <AvatarUploader
              profileImage={profileImage || '/placeholder.svg'}
              onSuccess={(data) =>
                updateProfile({
                  photoUrl: data.url,
                })
              }
            />

            <div className="flex-1 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <div className="flex items-center gap-4 flex-1">
                  <div className="flex items-center flex-1">
                    <Input
                      id="firstName"
                      name="firstName"
                      value={loanOfficerData.firstName}
                      onChange={handleChange}
                      placeholder="Your first name"
                    />
                  </div>
                  <div className="flex items-center flex-1">
                    <Input
                      id="lastName"
                      name="lastName"
                      value={loanOfficerData.lastName}
                      onChange={handleChange}
                      placeholder="Your last name"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  name="companyName"
                  value={loanOfficerData.companyName}
                  onChange={handleChange}
                  placeholder="Your company name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="licenseNumber">License Number</Label>
                <Input
                  id="licenseNumber"
                  name="licenseNumber"
                  value={loanOfficerData.licenseNumber}
                  onChange={handleChange}
                  placeholder="Your real estate license number"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Cell Phone</Label>
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 mr-2 text-tax-neutralGray" />
                    <Input
                      id="phoneNumber"
                      name="phoneNumber"
                      value={loanOfficerData.phoneNumber}
                      onChange={handleChange}
                      placeholder="(123) 456-7890"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 mr-2 text-tax-neutralGray" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={loanOfficerData.emailAddress}
                      onChange={handleChange}
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <div className="flex items-center">
                    <Link className="w-4 h-4 mr-2 text-tax-neutralGray" />
                    <Input
                      id="website"
                      name="website"
                      value={loanOfficerData.website}
                      onChange={handleChange}
                      placeholder="https://yoursite.com"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-end gap-4">
                <div className="space-y-2 flex-1 w-full">
                  <Label htmlFor="identifier">My Invite Link</Label>
                  <Input
                    id="identifier"
                    name="identifier"
                    value={`${window.location.origin}?loan-officer=${profileData?._id}`}
                    disabled
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleCopyLink}
                  className="bg-primary hover:bg-primary/90 w-full md:w-fit"
                >
                  Copy my link
                </Button>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-4">
            <Button
              type="submit"
              className="bg-primary hover:bg-primary/90 w-full md:w-fit"
            >
              Save Profile
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default LoanOfficerProfileForm;
